import React, { useEffect, useState } from "react";
import { getAdaptiveSecurityAudits } from "../../api/adaptiveSecurityAuditService";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

export default function TrustHistoryPage() {
    const [audits, setAudits] = useState([]);
    const [userId, setUserId] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        loadAudits();
    }, []);

    const loadAudits = async () => {
        try {
            setLoading(true);
            setError("");

            const data = await getAdaptiveSecurityAudits();
            setAudits(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load trust history."
            );
        } finally {
            setLoading(false);
        }
    };

    const filteredAudits = userId
        ? audits.filter((x) => String(x.userId) === String(userId))
        : audits;

    const chartData = filteredAudits
        .slice()
        .reverse()
        .map((item) => ({
            date: item.createdAt
                ? new Date(item.createdAt).toLocaleDateString()
                : "�",
            score: item.trustScore,
        }));

    return (
        <div className="max-w-7xl mx-auto p-6 bg-white rounded shadow">
            <h2 className="text-2xl font-bold mb-4">Trust Score History</h2>

            {error && <p className="text-red-600 mb-3">{error}</p>}

            <div className="flex gap-3 mb-4">
                <input
                    type="number"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Filter by User ID"
                    className="border p-2 rounded"
                />

                <button
                    onClick={loadAudits}
                    className="bg-blue-700 text-white px-4 py-2 rounded"
                >
                    Refresh
                </button>
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : filteredAudits.length === 0 ? (
                <p className="text-gray-500">No trust history records found.</p>
            ) : (
                <>
                    {chartData.length > 0 && (
                        <div className="h-72 mb-6 border rounded p-4 bg-gray-50">
                            <h3 className="font-bold mb-3">Trust Score Trend</h3>

                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis domain={[0, 100]} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="score" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    <table className="w-full border text-sm">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border p-2">Date</th>
                                <th className="border p-2">User</th>
                                <th className="border p-2">Shipment</th>
                                <th className="border p-2">Trust Score</th>
                                <th className="border p-2">Risk Level</th>
                                <th className="border p-2">Decision</th>
                                <th className="border p-2">Reason</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredAudits.map((item) => (
                                <tr key={item.id}>
                                    <td className="border p-2">
                                        {item.createdAt
                                            ? new Date(item.createdAt).toLocaleString()
                                            : "�"}
                                    </td>
                                    <td className="border p-2">
                                        {item.userName || item.userId || "�"}
                                    </td>
                                    <td className="border p-2">
                                        {item.shipmentId || "�"}
                                    </td>
                                    <td className="border p-2">
                                        {item.trustScore}
                                    </td>
                                    <td className="border p-2">
                                        {item.riskLevel}
                                    </td>
                                    <td className="border p-2">
                                        {item.blockOperation
                                            ? "Blocked"
                                            : item.requireTwoFactor
                                                ? "2FA Required"
                                                : item.allowAccess
                                                    ? "Allowed"
                                                    : "Denied"}
                                    </td>
                                    <td className="border p-2">
                                        {item.reason || "�"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}